/**
 * @file index.tsx
 *
 * @fileoverview Renders the appropriate badge depending on the state of
 * a resume contact request.
 */
import React from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { ResumeContact } from '~/jobiqo/types/types';
import { Badge } from '~/vcl/atoms/03-badge';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { parseUniversalDate } from '~/jobiqo/utils/dates';

interface ResumeContactBadgeProps {
  /**
   * The resume contact request to check the state for.
   */
  resumeContact?: ResumeContact;
  /**
   * If contact requests need approval from applicants.
   *
   * @default true
   */
  needsApproval?: boolean;
}

export const ResumeContactBadge = ({
  resumeContact,
  needsApproval = true,
}: ResumeContactBadgeProps) => {
  const intl = useIntl();

  if (!needsApproval) {
    return (
      <Badge
        data-test-id="recruiter_resume_contact_badge_no_approved"
        badgeStyle="success"
      >
        <FormattedMessage
          id="recruiter_resume_contact_badge_no_approved"
          defaultMessage="Access granted"
        />
      </Badge>
    );
  }

  switch (resumeContact?.state) {
    case 'RESUME_CONTACT_STATUS_APPROVED':
      return (
        <div style={{ textAlign: 'center' }}>
          <Badge
            data-test-id="recruiter_resume_contact_badge_status_approved"
            badgeStyle="success"
          >
            <FormattedMessage
              id="recruiter_resume_contact_badge_status_approved"
              defaultMessage="Contact request approved"
            />
          </Badge>
          {resumeContact?.changed && (
            <Text color="gray4" margin="0" fontSize={2} marginTop={4}>
              <span
                title={intl.formatMessage({
                  id: 'resume_contact_badge_approved_date_title',
                  defaultMessage: 'Date when the contact request was approved',
                })}
              >
                <FormattedDate
                  value={parseUniversalDate(resumeContact.changed)}
                  day="2-digit"
                  month="2-digit"
                  year="numeric"
                />
              </span>
            </Text>
          )}
        </div>
      );
    case 'RESUME_CONTACT_STATUS_PENDING':
      return (
        <div style={{ textAlign: 'center' }}>
          <Badge
            data-test-id="recruiter_resume_contact__badge_status_pending"
            badgeStyle="warning"
          >
            <FormattedMessage
              id="recruiter_resume_contact__badge_status_pending"
              defaultMessage="Contact request pending"
            />
          </Badge>
          {resumeContact?.changed && (
            <Text color="gray4" margin="0" fontSize={2} marginTop={4}>
              <span
                title={intl.formatMessage({
                  id: 'resume_contact_badge_pending_date_title',
                  defaultMessage: 'Date when the contact request was sent',
                })}
              >
                <FormattedDate
                  value={parseUniversalDate(resumeContact.changed)}
                  day="2-digit"
                  month="2-digit"
                  year="numeric"
                />
              </span>
            </Text>
          )}
        </div>
      );
    case 'RESUME_CONTACT_STATUS_REVOKED':
      return (
        <Badge
          data-test-id="recruiter_resume_contact_badge_status_revoked"
          badgeStyle="danger"
        >
          <FormattedMessage
            id="recruiter_resume_contact_badge_status_revoked"
            defaultMessage="Contact request revoked"
          />
        </Badge>
      );
    default:
      return null;
  }
};
export default ResumeContactBadge;
